<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import TermsPage from './pages/TermsPage.vue';
import PrivacyPage from './pages/PrivacyPage.vue';

// /terms and /privacy are generated by scripts/build-legal-pages.py from legal/.
// CloudFront serves index.html for every path, so the path decides what renders;
// the rest of this file is the home page.
const page = window.location.pathname.replace(/\/+$/, '').toLowerCase();

const ICONS = {
  eye: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/></svg>',
  pencil: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4z"/></svg>',
  code: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>',
  users: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>'
};

const sections = [
  {
    id: 'one',
    image: '/images/typewriter2.jpg',
    title: 'Who We Are',
    paragraphs: [
      'Steeltype is a web design and software development company that specializes in connecting small businesses to the cloud.',
      'Our mission is to empower clients to decide their destiny by providing them with easy and reliable access to digital strategy, tactics, and execution.',
      'We are a cloud-based small business. The fastest way to reach us is to call or text our office at (317) 735-6510.',
      'With well over a decade of experience to draw from, our team has the expert decision-making and problem-solving skills to navigate the constantly evolving field of digital technologies.'
    ]
  },
  {
    id: 'two',
    image: '/images/keyboard.jpg',
    title: 'What we do',
    intro: [
      'We build practical solutions to complex problems using modern technology in order to make our clients more efficient, effective, and timely in everything they do.',
      'We provide our clients with four areas of service:'
    ],
    services: [
      { icon: 'eye', label: 'Discovery' },
      { icon: 'pencil', label: 'Design' },
      { icon: 'code', label: 'Development' },
      { icon: 'users', label: 'Support' }
    ],
    details: [
      { term: 'Discovery:', body: "Our holistic analysis leads to a thorough understanding of each technology environment and it's strategic challenges. We believe that understanding is the key to transforming technical information into powerful insights." },
      { term: 'Design:', body: 'Our visual and technical designs begin with the careful consideration of all client objectives and then intertwine to achieve them. We consider drawings and layouts to be the physical reflection of purpose, planning, and intention.' },
      { term: 'Development:', body: 'Our development strategy prioritizes practicality and craftsmanship. We recognize that dreams only become reality through careful preparation and hard work.' },
      { term: 'Support:', body: 'Our technical expertise provides our clients with a variety of cost-effective hosting and support options. We are committed to ensuring our clients have the ability to control their long-term costs.' }
    ]
  },
  {
    id: 'products',
    image: '/images/idea.jpg',
    title: 'What We Make',
    intro: [
      'Alongside client work we build and sell our own software. Each one is a desktop product you buy once, with no subscription and no account to create.'
    ],
    // Taglines here must match what each product site says about itself. A
    // company page that describes a product differently from the product is
    // the first thing a careful reader notices.
    products: [
      {
        name: 'ArchiveHive',
        tagline: 'Preserve the whole drive. Keep the evidence.',
        href: 'https://archivehive.app',
        body: 'An archival imaging tool for Windows and macOS. It copies a drive sector by sector into open, published formats, records a checksum for every chunk and the errors it could not read, and puts recovery instructions inside the archive itself. Reading, verifying, and repairing an archive never needs a licence.'
      },
      {
        name: 'CrocProbe',
        tagline: 'Inspect a real iPhone from Windows or macOS.',
        href: 'https://crocprobe.com',
        body: 'A desktop tool for looking at what an iPhone actually reports about itself, from a computer, without an account or a cloud service in between.'
      }
    ]
  },
  {
    id: 'three',
    image: '/images/started.jpg',
    title: 'How We Work',
    paragraphs: [
      'We provide a free initial consultation during which we will ask you a short series of discovery questions that will allow us to better understand your specific needs and challenges.',
      'When starting a project we will send you a <i>“Statement of Work”</i> and a <i>“Terms and Conditions”</i> document that together form a contract describing the work to be performed.',
      'These documents will also divide the work to be performed into several deliverables. We will present each deliverable for your approval before work continues.',
      'We will issue an initial invoice that must be paid before the project starts, additional invoices for each completed deliverable, and a final invoice when the project is completed.',
      'Our process is constantly undergoing refinement to better align with our company ethos of efficient, effective, and timely service. We engage each client with an approach specially tailored to their needs.'
    ]
  }
];

function nextAnchor(index) {
  return index < sections.length - 1 ? sections[index + 1].id : 'footer';
}
function iconMarkup(name) { return ICONS[name] ?? ''; }

const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY || '1x00000000000000000000AA';

const headerRef = ref(null);
const sectionRefs = ref([]);
function setSectionRef(el, index) { if (el) sectionRefs.value[index] = el; }

const headerOpacity = ref(1);
const activeBgId = ref(null);
const status = ref(null);

function dismissStatus() { status.value = null; }

function smoothScrollTo(targetId, event) {
  if (event) event.preventDefault();
  const el = document.getElementById(targetId);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function onScroll() {
  if (!headerRef.value) return;
  const rect = headerRef.value.getBoundingClientRect();
  const height = rect.height || window.innerHeight;
  const scrolledPast = Math.max(0, -rect.top);
  headerOpacity.value = Math.max(0, Math.min(1, 1 - scrolledPast / height));
}

let observer = null;

onMounted(() => {
  const params = new URLSearchParams(window.location.search);
  const value = params.get('contact');
  if (value === 'success' || value === 'failure') status.value = value;

  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  observer = new IntersectionObserver(
    (entries) => {
      const crossing = entries.find((e) => e.isIntersecting);
      if (crossing) {
        activeBgId.value = crossing.target.id;
      } else {
        const stillCrossing = sectionRefs.value.find((el) => {
          const r = el.getBoundingClientRect();
          const mid = window.innerHeight * 0.5;
          return r.top <= mid && r.bottom >= mid;
        });
        activeBgId.value = stillCrossing ? stillCrossing.id : null;
      }
    },
    { rootMargin: '-50% 0px -50% 0px', threshold: 0 }
  );

  sectionRefs.value.forEach((el) => observer.observe(el));
});

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll);
  observer?.disconnect();
});
</script>

<template>
  <TermsPage v-if="page === '/terms'" />
  <PrivacyPage v-else-if="page === '/privacy'" />
  <template v-else>
  <div v-if="status === 'success'" id="thanks" @click="dismissStatus">
    Thank you, we've received your contact request and will respond to you shortly!
  </div>
  <div v-else-if="status === 'failure'" id="error" @click="dismissStatus">
    Sorry, we weren't able to send your contact request. Please send an email to us at
    <a href="mailto:errors@steeltype.io">errors@steeltype.io</a>.
  </div>

  <div
    v-for="section in sections"
    :key="`${section.id}-bg`"
    class="main-bg"
    :class="{ active: activeBgId === section.id }"
    :style="{ backgroundImage: `url('/images/overlay.png'), url('${section.image}')` }"
  ></div>

  <section id="header" ref="headerRef">
    <header class="major" :style="{ opacity: headerOpacity }">
      <h1>Steeltype</h1>
      <p>Ideas Organized, Visions Realized.</p>
    </header>
    <div class="container">
      <ul class="actions">
        <li>
          <a href="#one" class="special button" @click="smoothScrollTo('one', $event)">Begin</a>
        </li>
      </ul>
    </div>
  </section>

  <section
    v-for="(section, index) in sections"
    :key="section.id"
    :id="section.id"
    :ref="(el) => setSectionRef(el, index)"
    class="special main"
  >
    <div class="container">
      <div class="content">
        <header class="major">
          <h2>{{ section.title }}</h2>
        </header>

        <template v-if="section.paragraphs">
          <p v-for="(p, i) in section.paragraphs" :key="i" v-html="p"></p>
        </template>

        <template v-else-if="section.products">
          <p v-for="(p, i) in section.intro" :key="`intro-${i}`">{{ p }}</p>
          <ul class="products">
            <li v-for="product in section.products" :key="product.name">
              <h3>
                <a :href="product.href" rel="noopener">{{ product.name }}</a>
              </h3>
              <p class="tagline">{{ product.tagline }}</p>
              <p>{{ product.body }}</p>
            </li>
          </ul>
        </template>

        <template v-else-if="section.services">
          <p v-for="(p, i) in section.intro" :key="`intro-${i}`">{{ p }}</p>
          <ul class="icons-grid">
            <li v-for="service in section.services" :key="service.label">
              <span class="major icon" v-html="iconMarkup(service.icon)"></span>
              <h3>{{ service.label }}</h3>
            </li>
          </ul>
          <p v-for="detail in section.details" :key="detail.term">
            <strong>{{ detail.term }}</strong> {{ detail.body }}
          </p>
        </template>
      </div>
      <a
        :href="`#${nextAnchor(index)}`"
        class="goto-next"
        @click="smoothScrollTo(nextAnchor(index), $event)"
      >Next</a>
    </div>
  </section>

  <section id="footer">
    <div class="container">
      <header class="major">
        <h2>Get in touch</h2>
      </header>
      <form action="/api/contact" method="post">
        <div class="form-grid">
          <input id="name" name="name" type="text" placeholder="Name" autocomplete="name" required />
          <input id="email" name="email" type="email" placeholder="Email" autocomplete="email" required />
          <textarea id="message" name="message" placeholder="Message" rows="4" required maxlength="5000"></textarea>
          <div
            class="cf-turnstile"
            :data-sitekey="turnstileSiteKey"
            data-theme="light"
          ></div>
          <div class="hp-field" aria-hidden="true">
            <label>
              Leave this field empty
              <input type="text" name="company" tabindex="-1" autocomplete="off" />
            </label>
          </div>
          <ul class="actions">
            <li>
              <input type="submit" class="special" value="Send Message" />
            </li>
          </ul>
        </div>
      </form>
    </div>
    <footer>
      <ul class="copyright">
        <li>&copy; Steeltype LLC</li>
        <li><a href="/terms">Terms</a></li>
        <li><a href="/privacy">Privacy</a></li>
      </ul>
    </footer>
  </section>
  </template>
</template>
